import React, { useMemo, memo, useEffect, useRef } from 'react';
import { Icon, Tag, Typography } from '@scale/scaleui';
import { FeedbackPriority, belongsToStep } from '@scale/shared/types/chat/contributorFeedback';
import IssueGroupAccordion from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/IssueGroupAccordion';
import {
  FeedbackType,
  InteractionStepType,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { StepParamsAuxiliary } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.computed';
import { useContributorFeedbackStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { ContributorFeedbackStepContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/context';
import { twMerge } from 'tailwind-merge';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import _ from 'lodash';
import {
  ChatTheme,
  useChatThemeStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';

interface FeedbackHeaderProps {
  className?: string;
  numUnaddressedFeedback: number;
  areUnaddressedBlockingFeedbackItems: boolean;
  areUnaddressedNonBlockingFeedbackItems: boolean;
}

const FeedbackHeader: React.FC<FeedbackHeaderProps> = ({
  className,
  numUnaddressedFeedback,
  areUnaddressedBlockingFeedbackItems,
  areUnaddressedNonBlockingFeedbackItems,
}) => {
  return (
    <div
      className={twMerge(
        'flex flex-row w-full gap-1.5 items-center rounded-t-md bg-neutral-50',
        areUnaddressedBlockingFeedbackItems
          ? 'border-b border-danger-500 bg-danger-100'
          : areUnaddressedNonBlockingFeedbackItems
          ? 'border-b border-warning-500 bg-warning-100'
          : '',

        className,
      )}
    >
      <div className="flex flex-row p-1.5 gap-0.5 items-center">
        <Icon variant="black" icon={['fas', 'flag']} />

        <div className="flex flex-row items-center gap-1">
          <Typography.Subtitle4 className="text-xs">Detected potential issues</Typography.Subtitle4>
          {!!numUnaddressedFeedback && (
            <Tag className="bg-danger-50" size="sm">
              <Typography.Paragraph3 className="text-[10px] text-danger-500">
                {numUnaddressedFeedback} unresolved
              </Typography.Paragraph3>
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
};

type ContributorAutomatedFeedbackProps<T extends InteractionStepType> = {
  step: (StepParamsForType<T> & StepParamsAuxiliary) | StepParamsForTypeWithStage<T>;
};

const ContributorAutomatedFeedbackBox: React.FC<
  ContributorAutomatedFeedbackProps<InteractionStepType>
> = ({ step }) => {
  const stepWithIndexKey = constructStepChildId(step);
  const feedback = useContributorFeedbackStore(s =>
    s.feedback.filter(({ childId }) => belongsToStep(childId, stepWithIndexKey)),
  );
  const hasLinterError = useChatStoreExtract(
    state => state.stepLintersMetadata[step.responseIndex]?.hasLinterError,
  );
  const targetRef = useRef<HTMLDivElement>(null);
  const theme = useChatThemeStore(state => state.theme);

  // If there is a linter error triggered during step submission, scroll to the feedback box
  useEffect(() => {
    if (hasLinterError) {
      targetRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hasLinterError]);

  const {
    automatedFeedback,
    blockingFeedbackItems,
    otherFeedbackItems,
    unaddressedFeedbackItems,
    areUnaddressedBlockingFeedbackItems,
    areUnaddressedNonBlockingFeedbackItems,
  } = useMemo(() => {
    const automatedFeedback = feedback.filter(
      feedbackItem => feedbackItem.type !== FeedbackType.HumanReviewerFeedback,
    );
    const [blockingFeedbackItems, otherFeedbackItems] = _.partition(
      automatedFeedback,
      feedbackItem => feedbackItem.isBlocking,
    );
    const unaddressedFeedbackItems = automatedFeedback.filter(
      feedbackItem => !feedbackItem.action?.actionTaken,
    );
    const areUnaddressedBlockingFeedbackItems = !!blockingFeedbackItems.length;
    const areUnaddressedNonBlockingFeedbackItems = otherFeedbackItems.some(
      feedbackItem => !feedbackItem.action?.actionTaken,
    );
    return {
      automatedFeedback,
      blockingFeedbackItems,
      otherFeedbackItems,
      unaddressedFeedbackItems,
      areUnaddressedBlockingFeedbackItems,
      areUnaddressedNonBlockingFeedbackItems,
    };
  }, [feedback]);

  // Darkmode support is built-in for standard `bg-` tailwind classes (i.e bg-neutral-200).
  // Since we're using custom colors, we need to handle darkmode manually.
  const bgColor = theme === ChatTheme.Light ? 'bg-[#F7FAFF]' : 'bg-[#101217]';

  return (
    <div ref={targetRef}>
      {automatedFeedback.length ? (
        <ContributorFeedbackStepContext.Provider value={{ stepWithIndexKey }}>
          <div
            className={twMerge(
              'flex flex-col lg:w-60 xl:w-80 rounded-md overflow-hidden',
              areUnaddressedBlockingFeedbackItems
                ? 'border border-danger-500'
                : areUnaddressedNonBlockingFeedbackItems
                ? 'border border-warning-500'
                : 'border border-neutral-200',
              bgColor,
            )}
          >
            <FeedbackHeader
              numUnaddressedFeedback={unaddressedFeedbackItems.length}
              areUnaddressedNonBlockingFeedbackItems={areUnaddressedNonBlockingFeedbackItems}
              areUnaddressedBlockingFeedbackItems={areUnaddressedBlockingFeedbackItems}
            />

            {/* High Priority Feedback */}
            {!!blockingFeedbackItems.length && (
              <IssueGroupAccordion
                accordionClassName={bgColor}
                headerTitle="Required Fixes"
                areUnaddressedFeedbackItems={areUnaddressedBlockingFeedbackItems}
                feedbackItems={blockingFeedbackItems}
              />
            )}

            {/* Low Priority Feedback */}
            {!!otherFeedbackItems.length && (
              <IssueGroupAccordion
                accordionClassName={bgColor}
                headerTitle={FeedbackPriority.LOW}
                areUnaddressedFeedbackItems={areUnaddressedNonBlockingFeedbackItems}
                feedbackItems={otherFeedbackItems}
              />
            )}
          </div>
        </ContributorFeedbackStepContext.Provider>
      ) : (
        // Empty div to emulate a two-width layout
        <div className="flex lg:w-60 xl:w-80" />
      )}
    </div>
  );
};

export default memo(ContributorAutomatedFeedbackBox);
